import { useSyncExternalStore } from 'react'

type TSize = [number, number]

/**
 * Get the current size of the Viewport. Do not call this excessively, as it may
 * cause performance issues in WebKit. Querying innerWidth/height triggers a
 * relayout of the page.
 */
const getViewportSize = (): TSize => {
  if (window.visualViewport) {
    // visualViewport is a new prop intended for this exact behavior, prefer it
    // over all else when available
    // https://developer.mozilla.org/en-US/docs/Web/API/Visual_Viewport_API
    return [window.visualViewport.width, window.visualViewport.height] as const
  }

  return [
    window.innerWidth,
    // window.innerHeight gets updated when a user opens the soft keyboard, so
    // it should be preferred over documentElement.clientHeight
    // Want more? https://blog.opendigerati.com/the-eccentric-ways-of-ios-safari-with-the-keyboard-b5aa3f34228d
    window.innerHeight,
  ] as const
}

let cachedSize: TSize | undefined

const getSnapshot = (): TSize => {
  const size = getViewportSize()

  if (cachedSize && cachedSize[0] === size[0] && cachedSize[1] === size[1]) {
    return cachedSize
  }

  cachedSize = size
  return cachedSize
}

const subscribe = (onStoreChange: () => void) => {
  const effectTwice = () => {
    onStoreChange()
    // Closing the OSK in iOS does not immediately update the visual viewport
    // size :<
    setTimeout(onStoreChange, 1000)
  }

  window.addEventListener('resize', effectTwice)
  // From the top of my head this used to be required for older browsers since
  // this didn't trigger a resize event. Keeping it in to be safe.
  window.addEventListener('orientationchange', effectTwice)
  // This is needed on iOS to resize the viewport when the Virtual/OnScreen
  // Keyboard opens. This does not trigger any other event, or the standard
  // resize event.
  window.visualViewport?.addEventListener('resize', effectTwice)

  return () => {
    window.removeEventListener('resize', effectTwice)
    window.removeEventListener('orientationchange', effectTwice)
    window.visualViewport?.removeEventListener('resize', effectTwice)
  }
}

/**
 * Returns the viewport size. This can also be used as a dependency in a
 * useEffect to trigger an update when the browser resizes.
 */
export const useViewportSize = () => useSyncExternalStore(subscribe, getSnapshot)
